import React, { useEffect, useMemo, useState } from "react";
import { api, codeClasses } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { MagnifyingGlass } from "@phosphor-icons/react";

export default function DiscriminatorSearchDialog({ open, onOpenChange, onOpenProtocol }) {
  const [q, setQ] = useState("");
  const [protocols, setProtocols] = useState([]);

  useEffect(() => {
    if (!open) return;
    (async () => {
      const { data } = await api.get("/protocols");
      // Load full docs (they include discriminators already in list endpoint)
      setProtocols(data);
    })();
  }, [open]);

  const results = useMemo(() => {
    if (!q || q.length < 2) return [];
    const s = q.toLowerCase();
    const out = [];
    for (const p of protocols) {
      for (const d of p.discriminators || []) {
        const hay = `${d.name || ""} ${d.description || ""} ${d.hint || ""}`.toLowerCase();
        if (hay.includes(s)) out.push({ protocol: p, disc: d });
      }
    }
    return out.slice(0, 200);
  }, [q, protocols]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MagnifyingGlass size={22} /> Ricerca globale discriminatori
          </DialogTitle>
          <DialogDescription>
            Cerca un discriminatore per nome, descrizione o suggerimento tra tutti i protocolli attivi.
          </DialogDescription>
        </DialogHeader>
        <Input
          autoFocus
          data-testid="global-disc-search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Es. temperatura, dolore severo, GCS…"
          className="h-12"
        />
        <div className="mt-3 flex-1 overflow-auto">
          {q.length >= 2 && results.length === 0 && (
            <div className="text-sm text-slate-500 text-center py-6">Nessun risultato per "{q}".</div>
          )}
          {results.length > 0 && (
            <div className="text-xs text-slate-500 mb-2">{results.length} risultat{results.length === 1 ? "o" : "i"}</div>
          )}
          <ul className="space-y-1">
            {results.map(({ protocol, disc }) => (
              <li
                key={`${protocol.protocol_id}-${disc.id}`}
                className="p-3 rounded-md border border-slate-200 hover:bg-slate-50 cursor-pointer flex items-center gap-3"
                data-testid={`global-disc-result-${disc.id}`}
                onClick={() => { onOpenProtocol(protocol.protocol_id, disc.id); onOpenChange(false); }}
              >
                <span className={`chip-code text-xs ${codeClasses(disc.code)}`}>{disc.code}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{disc.name}</div>
                  <div className="text-xs text-slate-500 truncate">{protocol.name} · v.{protocol.version}</div>
                </div>
                {disc.active === false && (
                  <span className="text-[10px] uppercase tracking-widest text-slate-500">non attivo</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      </DialogContent>
    </Dialog>
  );
}
